import React, { useEffect, useMemo, useState } from "react";
import {
    Alert,
    alpha,
    Box,
    Button,
    Chip,
    CircularProgress,
    Container,
    Divider,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { underControl } from "../../../redux/userRelated/userSlice";
import Popup from "../../../components/Popup";
import SetupNextStepDialog from "../../../components/SetupNextStepDialog";
import { FEEDBACK, formatAdminError, ERROR_HINTS } from "../../../utils/feedbackMessages";
import AddCardIcon from "@mui/icons-material/AddCard";
import { useHomeroomTeacherTerm } from "../../../hooks/useHomeroomTeacherTerm";
import { bulkCreateClasses, createClassWithLevel, getLevelCodes } from "../../../redux/sclassRelated/sclassHandle";
import StreamSectionsInput from "../../../components/class/StreamSectionsInput";
import {
    buildClassDisplayName,
    formatLevelLabel,
    parseStreamsInput,
    previewClassNames,
} from "../../../utils/classStreamUtils";

const FALLBACK_LEVEL_CODES = {
    Primary: ["Std1", "Std2", "Std3", "Std4", "Std5", "Std6", "Std7", "Std8"],
    Secondary: ["Form1", "Form2", "Form3", "Form4"],
    University: ["Year1", "Year2", "Year3", "Year4", "Year5"],
};

const SCHOOL_TYPE_LEVELS = {
    Primary: ["Primary"],
    Secondary: ["Secondary"],
    University: ["University"],
    Combined: ["Primary", "Secondary", "University"],
};

const getExamInfo = (levelCode) => {
    const code = String(levelCode || "").trim();
    if (code === "Std8") return { examType: "PSLCE", message: "This class will sit PSLCE at end of year" };
    if (code === "Form2") return { examType: "JCE", message: "This class will sit JCE at end of year" };
    if (code === "Form4") return { examType: "MSCE", message: "This class will sit MSCE at end of year" };
    return null;
};

const AddClass = () => {
    const [studentCapacity, setStudentCapacity] = useState("");
    const [room, setRoom] = useState("");
    const [educationLevel, setEducationLevel] = useState("Secondary");
    const [levelCode, setLevelCode] = useState("");
    const [stream, setStream] = useState("");
    const [levelCodes, setLevelCodes] = useState(FALLBACK_LEVEL_CODES);
    const [academicYear, setAcademicYear] = useState("");
    const [semester, setSemester] = useState("Semester 1");
    const [department, setDepartment] = useState("");
    const [faculty, setFaculty] = useState("");
    const [programme, setProgramme] = useState("");

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const userState = useSelector((state) => state.user);
    const { status, currentUser, response, error, tempDetails } = userState;

    const adminID = currentUser._id;
    const schoolType = currentUser.schoolType || "Secondary";
    const homeroomTerm = useHomeroomTeacherTerm();

    const [loader, setLoader] = useState(false);
    const [message, setMessage] = useState("");
    const [showPopup, setShowPopup] = useState(false);
    const [popupSeverity, setPopupSeverity] = useState(undefined);
    const [createdClassId, setCreatedClassId] = useState("");
    const [showNextStep, setShowNextStep] = useState(false);
    const [addMode, setAddMode] = useState("bulk_secondary");

    const showBulkSecondary = schoolType === "Secondary" || schoolType === "Combined";
    const showBulkPrimary = schoolType === "Primary" || schoolType === "Combined";
    const isBulkMode = addMode === "bulk_secondary" || addMode === "bulk_primary";
    const isSectionsMode = addMode === "sections";
    const isSingleMode = addMode === "single";

    const educationOptions = useMemo(() => SCHOOL_TYPE_LEVELS[schoolType] || ["Secondary"], [schoolType]);

    const bulkLevelCodes = useMemo(() => {
        if (addMode === "bulk_secondary") {
            return levelCodes.Secondary || FALLBACK_LEVEL_CODES.Secondary;
        }
        if (addMode === "bulk_primary") {
            return levelCodes.Primary || FALLBACK_LEVEL_CODES.Primary;
        }
        return [];
    }, [addMode, levelCodes]);

    const availableLevelCodes = useMemo(
        () => levelCodes[educationLevel] || FALLBACK_LEVEL_CODES[educationLevel] || [],
        [educationLevel, levelCodes]
    );

    const parsedStreams = useMemo(() => parseStreamsInput(stream), [stream]);

    const sclassName = useMemo(() => {
        if (!levelCode) return "";
        if (parsedStreams.length !== 1) return "";
        return buildClassDisplayName(levelCode, parsedStreams[0]);
    }, [levelCode, parsedStreams]);

    const classNamePreview = useMemo(() => {
        if (isBulkMode) {
            return previewClassNames(bulkLevelCodes, stream);
        }
        if (isSectionsMode || isSingleMode) {
            if (!levelCode) return [];
            return previewClassNames([levelCode], stream);
        }
        return [];
    }, [isBulkMode, isSectionsMode, isSingleMode, bulkLevelCodes, levelCode, stream]);

    const examInfo = useMemo(() => getExamInfo(levelCode), [levelCode]);

    const appendQuickSection = (label) => {
        const current = parseStreamsInput(stream).filter((s) => s !== "");
        if (!current.includes(label)) {
            setStream([...current, label].join(", "));
        }
    };

    useEffect(() => {
        const nextLevel = educationOptions[0] || "Secondary";
        setEducationLevel((prev) => (educationOptions.includes(prev) ? prev : nextLevel));
    }, [educationOptions]);

    useEffect(() => {
        if (!availableLevelCodes.length) {
            setLevelCode("");
            return;
        }
        setLevelCode((prev) => (availableLevelCodes.includes(prev) ? prev : availableLevelCodes[0]));
    }, [availableLevelCodes]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const result = await dispatch(getLevelCodes());
                if (!cancelled && result && typeof result === "object") {
                    setLevelCodes({
                        Primary: Array.isArray(result.Primary) ? result.Primary : FALLBACK_LEVEL_CODES.Primary,
                        Secondary: Array.isArray(result.Secondary) ? result.Secondary : FALLBACK_LEVEL_CODES.Secondary,
                        University: Array.isArray(result.University) ? result.University : FALLBACK_LEVEL_CODES.University,
                    });
                }
            } catch {
                /* fallback codes already set */
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [adminID, dispatch]);

    const fields = {
        schoolId: adminID,
        sclassName,
        educationLevel,
        levelCode,
        stream: stream.trim(),
        academicYear: educationLevel === "University" ? academicYear.trim() : "",
        semester: educationLevel === "University" ? semester : "",
        department: educationLevel === "University" ? department.trim() : "",
        faculty: educationLevel === "University" ? faculty.trim() : "",
        programme: educationLevel === "University" ? programme.trim() : "",
        studentCapacity: studentCapacity === "" ? undefined : studentCapacity,
        room: room.trim() || undefined,
        isExamClass: Boolean(examInfo),
        examType: examInfo?.examType || "",
    };

    const submitHandler = async (event) => {
        event.preventDefault();
        if (!educationLevel || !levelCode) {
            setMessage("Please select the education level and level code.");
            setShowPopup(true);
            return;
        }

        const streams = parseStreamsInput(stream);

        if (isSectionsMode) {
            const sectionsOnly = streams.filter((s) => s !== "");
            if (sectionsOnly.length < 1) {
                setMessage("Enter at least one section (e.g. A, B) to create separate classes.");
                setShowPopup(true);
                return;
            }
            setLoader(true);
            await dispatch(
                bulkCreateClasses({
                    schoolId: adminID,
                    educationLevel,
                    classTemplate: [{ levelCode, streams: sectionsOnly }],
                }),
            );
            return;
        }

        if (streams.length > 1) {
            setLoader(true);
            await dispatch(
                bulkCreateClasses({
                    schoolId: adminID,
                    educationLevel,
                    classTemplate: [{ levelCode, streams }],
                }),
            );
            return;
        }

        if (!sclassName) {
            setMessage("Please select the education level and level code.");
            setShowPopup(true);
            return;
        }

        setLoader(true);
        dispatch(
            createClassWithLevel({
                ...fields,
                stream: streams[0] || "",
                sclassName,
            }),
        );
    };

    const bulkSubmitHandler = async (event) => {
        event.preventDefault();
        const educationLevelBulk = addMode === "bulk_secondary" ? "Secondary" : "Primary";
        const codes = bulkLevelCodes;
        if (!codes.length) {
            setMessage("No level codes available for bulk create.");
            setShowPopup(true);
            return;
        }
        const streams = parseStreamsInput(stream);
        setLoader(true);
        const classTemplate = codes.map((code) => ({
            levelCode: code,
            streams,
        }));
        await dispatch(
            bulkCreateClasses({
                schoolId: adminID,
                educationLevel: educationLevelBulk,
                classTemplate,
            }),
        );
    };

    useEffect(() => {
        if (schoolType === "Primary") setAddMode("bulk_primary");
        else if (schoolType === "Secondary") setAddMode("bulk_secondary");
        else if (schoolType === "Combined") setAddMode("bulk_secondary");
        else setAddMode("single");
    }, [schoolType]);

    useEffect(() => {
        if (status === "added" && tempDetails?.bulk) {
            const { created = 0, skipped = 0 } = tempDetails;
            setMessage(`${FEEDBACK.classesBulkAdded(created, skipped)}\nProceed to add subjects for each class.`);
            setCreatedClassId("");
            setShowNextStep(true);
            dispatch(underControl());
            setLoader(false);
            return;
        }
        if (status === "added" && tempDetails) {
            setMessage(`${FEEDBACK.classAdded}\nProceed to add subjects for this class.`);
            setCreatedClassId(tempDetails._id || tempDetails.id || "");
            setShowNextStep(true);
            dispatch(underControl());
            setLoader(false);
            return;
        } else if (status === "failed") {
            setMessage(formatAdminError(response));
            setPopupSeverity("error");
            setShowPopup(true);
            setLoader(false);
        } else if (status === "error") {
            setMessage(formatAdminError(error, ERROR_HINTS.network));
            setPopupSeverity("error");
            setShowPopup(true);
            setLoader(false);
        }
    }, [status, navigate, error, response, dispatch, tempDetails]);

    return (
        <Box
            sx={{
                minHeight: "100%",
                py: { xs: 2, sm: 3 },
                background: (t) =>
                    t.palette.mode === "light"
                        ? `linear-gradient(180deg, ${alpha(t.palette.primary.main, 0.05)} 0%, ${t.palette.grey[50]} 24%)`
                        : alpha(t.palette.common.black, 0.12),
            }}
        >
            <Container maxWidth="sm">
                <Paper
                    elevation={0}
                    sx={{
                        borderRadius: 3,
                        overflow: "hidden",
                        border: (t) => `1px solid ${alpha(t.palette.divider, 0.9)}`,
                        boxShadow: (t) => `0 8px 32px ${alpha(t.palette.common.black, 0.06)}`,
                    }}
                >
                    <Box
                        sx={{
                            px: { xs: 2, sm: 3 },
                            py: 2.5,
                            background: (t) =>
                                `linear-gradient(115deg, ${t.palette.primary.dark} 0%, ${t.palette.primary.main} 50%, ${alpha(
                                    t.palette.primary.light,
                                    0.92
                                )} 100%)`,
                            color: "common.white",
                        }}
                    >
                        <Stack direction="row" alignItems="center" spacing={1.5}>
                            <AddCardIcon sx={{ fontSize: 32, opacity: 0.95 }} />
                            <Box>
                                <Typography variant="overline" sx={{ opacity: 0.9, letterSpacing: 1 }}>
                                    Curriculum
                                </Typography>
                                <Typography variant="h5" fontWeight={800}>
                                    Add class
                                </Typography>
                                <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.25 }}>
                                    Create separate classes per section (e.g. Form 1 A and Form 1 B), or add all forms at once.
                                </Typography>
                            </Box>
                        </Stack>
                    </Box>

                    <Box
                        component="form"
                        onSubmit={isBulkMode ? bulkSubmitHandler : submitHandler}
                        sx={{ p: { xs: 2, sm: 3 } }}
                    >
                        {(showBulkSecondary || showBulkPrimary) && (
                            <Stack spacing={2} sx={{ mb: 3 }}>
                                <Typography variant="subtitle2" fontWeight={700}>
                                    How do you want to add classes?
                                </Typography>
                                <ToggleButtonGroup
                                    value={addMode}
                                    exclusive
                                    onChange={(_, v) => v && setAddMode(v)}
                                    size="small"
                                    sx={{ flexWrap: "wrap" }}
                                >
                                    {showBulkSecondary ? (
                                        <ToggleButton value="bulk_secondary">
                                            All secondary (Form 1–4)
                                        </ToggleButton>
                                    ) : null}
                                    {showBulkPrimary ? (
                                        <ToggleButton value="bulk_primary">
                                            All primary (Std 1–8)
                                        </ToggleButton>
                                    ) : null}
                                    <ToggleButton value="sections">One level, sections A &amp; B</ToggleButton>
                                    <ToggleButton value="single">One class (advanced)</ToggleButton>
                                </ToggleButtonGroup>
                            </Stack>
                        )}

                        {isBulkMode ? (
                            <Stack spacing={2}>
                                <Alert severity="info" sx={{ borderRadius: 2 }}>
                                    Creates{" "}
                                    <strong>{classNamePreview.length || bulkLevelCodes.length}</strong> classes
                                    {classNamePreview.length > bulkLevelCodes.length
                                        ? ` (${bulkLevelCodes.length} levels × sections)`
                                        : ""}
                                    . Existing names are skipped.
                                </Alert>
                                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                    {bulkLevelCodes.map((code) => (
                                        <Chip key={code} label={formatLevelLabel(code)} size="small" variant="outlined" />
                                    ))}
                                </Stack>
                                <StreamSectionsInput
                                    value={stream}
                                    onChange={setStream}
                                    onQuickAdd={appendQuickSection}
                                    previewNames={classNamePreview}
                                    label="Sections for every level"
                                    placeholder="e.g. A, B — creates Form 1 A, Form 1 B, Form 2 A, Form 2 B, …"
                                    helperText="Enter A, B to get two separate classes per form. Leave blank for one class per form (no letter)."
                                />
                            </Stack>
                        ) : isSectionsMode ? (
                            <Stack spacing={2}>
                                <Alert severity="info" sx={{ borderRadius: 2 }}>
                                    Create <strong>separate classes</strong> for the same year (e.g.{" "}
                                    <strong>Form 1 A</strong> and <strong>Form 1 B</strong>), each with its own
                                    students and form teacher.
                                </Alert>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth>
                                            <InputLabel id="sections-education-label">Education level</InputLabel>
                                            <Select
                                                labelId="sections-education-label"
                                                label="Education level"
                                                value={educationLevel}
                                                onChange={(e) => setEducationLevel(e.target.value)}
                                            >
                                                {educationOptions.map((option) => (
                                                    <MenuItem key={option} value={option}>
                                                        {option}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth>
                                            <InputLabel id="sections-level-label">Level</InputLabel>
                                            <Select
                                                labelId="sections-level-label"
                                                label="Level"
                                                value={levelCode}
                                                onChange={(e) => setLevelCode(e.target.value)}
                                            >
                                                {availableLevelCodes.map((code) => (
                                                    <MenuItem key={code} value={code}>
                                                        {formatLevelLabel(code)}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                </Grid>
                                <StreamSectionsInput
                                    value={stream}
                                    onChange={setStream}
                                    onQuickAdd={appendQuickSection}
                                    previewNames={classNamePreview}
                                    placeholder="e.g. A, B — required for Form 1 A and Form 1 B"
                                    helperText="Each letter becomes its own class at the level you selected above."
                                />
                            </Stack>
                        ) : (
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                                    Level
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Set the education level first, then choose the matching Malawi level code.
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth>
                                    <InputLabel id="education-level-label">Education Level</InputLabel>
                                    <Select
                                        labelId="education-level-label"
                                        label="Education Level"
                                        value={educationLevel}
                                        onChange={(e) => setEducationLevel(e.target.value)}
                                    >
                                        {educationOptions.map((option) => (
                                            <MenuItem key={option} value={option}>
                                                {option}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth>
                                    <InputLabel id="level-code-label">Level Code</InputLabel>
                                    <Select
                                        labelId="level-code-label"
                                        label="Level Code"
                                        value={levelCode}
                                        onChange={(e) => setLevelCode(e.target.value)}
                                    >
                                        {availableLevelCodes.map((code) => (
                                            <MenuItem key={code} value={code}>
                                                {code}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12}>
                                <StreamSectionsInput
                                    value={stream}
                                    onChange={setStream}
                                    onQuickAdd={appendQuickSection}
                                    previewNames={classNamePreview}
                                    label="Class section (optional)"
                                    placeholder="e.g. A — or A, B to create multiple classes at once"
                                />
                            </Grid>
                            {examInfo ? (
                                <Grid item xs={12}>
                                    <Alert severity="info">{examInfo.message}</Alert>
                                </Grid>
                            ) : null}
                            {parsedStreams.length === 1 && sclassName ? (
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        required
                                        label="Class name"
                                        value={sclassName}
                                        InputProps={{ readOnly: true }}
                                        helperText="Auto-generated from level and section."
                                    />
                                </Grid>
                            ) : null}
                            {parsedStreams.length > 1 ? (
                                <Grid item xs={12}>
                                    <Alert severity="info" sx={{ borderRadius: 2 }}>
                                        Multiple sections selected — each will be created as a separate class. Assign
                                        form teachers under <strong>All classes</strong> after creation.
                                    </Alert>
                                </Grid>
                            ) : null}
                            {educationLevel === "University" ? (
                                <>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Academic Year"
                                            value={academicYear}
                                            onChange={(e) => setAcademicYear(e.target.value)}
                                            placeholder='e.g. "2024/2025"'
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth>
                                            <InputLabel id="semester-label">Semester</InputLabel>
                                            <Select
                                                labelId="semester-label"
                                                label="Semester"
                                                value={semester}
                                                onChange={(e) => setSemester(e.target.value)}
                                            >
                                                <MenuItem value="Semester 1">Semester 1</MenuItem>
                                                <MenuItem value="Semester 2">Semester 2</MenuItem>
                                                <MenuItem value="Full Year">Full Year</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Department"
                                            value={department}
                                            onChange={(e) => setDepartment(e.target.value)}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Faculty"
                                            value={faculty}
                                            onChange={(e) => setFaculty(e.target.value)}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Programme"
                                            value={programme}
                                            onChange={(e) => setProgramme(e.target.value)}
                                            placeholder='e.g. "BSc Computer Science"'
                                        />
                                    </Grid>
                                </>
                            ) : null}
                            <Grid item xs={12}>
                                <Alert severity="info" sx={{ borderRadius: 2 }}>
                                    After you add subjects to this class, set the{" "}
                                    {homeroomTerm.assignOnClass.toLowerCase()} under <strong>All classes</strong> or on
                                    the class details page — you can pick the teacher and their subjects in one step.
                                </Alert>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Student capacity"
                                    type="number"
                                    value={studentCapacity}
                                    onChange={(e) => setStudentCapacity(e.target.value)}
                                    inputProps={{ min: 1, step: 1 }}
                                    helperText="Optional. Enrolment is blocked when count reaches this limit."
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Room / venue"
                                    value={room}
                                    onChange={(e) => setRoom(e.target.value)}
                                    placeholder="e.g. Block B — Room 12"
                                />
                            </Grid>
                        </Grid>
                        )}

                        <Divider sx={{ my: 3 }} />

                        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} justifyContent="flex-end">
                            <Button variant="outlined" color="inherit" onClick={() => navigate(-1)} sx={{ borderRadius: 2 }}>
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                variant="contained"
                                size="large"
                                disabled={loader}
                                sx={{ borderRadius: 2, minWidth: 160, fontWeight: 700 }}
                            >
                                {loader ? (
                                    <CircularProgress size={26} color="inherit" />
                                ) : isBulkMode ? (
                                    addMode === "bulk_secondary" ? "Create all forms" : "Create all standards"
                                ) : isSectionsMode ? (
                                    `Create ${classNamePreview.length || ""} classes`.trim()
                                ) : parsedStreams.length > 1 ? (
                                    `Create ${classNamePreview.length} classes`
                                ) : (
                                    "Create class"
                                )}
                            </Button>
                        </Stack>
                    </Box>
                </Paper>
            </Container>
            <Popup
                message={message}
                setShowPopup={setShowPopup}
                showPopup={showPopup}
                severity={popupSeverity}
            />
            <SetupNextStepDialog
                open={showNextStep}
                onClose={() => setShowNextStep(false)}
                title="Classes created"
                description="Next step: add subjects for your new classes."
                actions={[
                    {
                        label: "Proceed to add subjects",
                        variant: "contained",
                        onClick: () => {
                            setShowNextStep(false);
                            navigate(
                                createdClassId
                                    ? `/Admin/addsubject/${createdClassId}`
                                    : "/Admin/subjects/chooseclass",
                            );
                        },
                    },
                    {
                        label: "Go to all classes",
                        variant: "outlined",
                        color: "inherit",
                        onClick: () => {
                            setShowNextStep(false);
                            navigate("/Admin/classes");
                        },
                    },
                ]}
            />
        </Box>
    );
};

export default AddClass;
